import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

const FIELD = { vendor_id: "vendor", bill_number: "bill number", bill_date: "date", total_amount: "total" };

// Existing canonical duplicate result. Override only when the server says the caller may.
export const DuplicatePanel = ({ dup, canOverride, busy, onOverride, onDismiss }) => {
  const [open, setOpen] = useState(dup.reasonRequired);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState(dup.reasonRequired ? "A reason is required to confirm a duplicate" : null);
  const fields = [...new Set((dup.matches || []).flatMap((m) => m.matched_fields || []))].map((f) => FIELD[f] || f);

  const submit = (e) => {
    e.preventDefault();
    if (!reason.trim()) return setErr("A reason is required to confirm a duplicate");
    setErr(null);
    onOverride(reason.trim());
  };

  return (
    <section role="alert" data-testid="duplicate-panel" className="rounded-md border-2 border-red-300 bg-red-50 p-4 space-y-3">
      <div className="flex gap-2 items-start">
        <AlertTriangle size={20} className="text-red-700 shrink-0 mt-0.5" aria-hidden />
        <div>
          <h2 className="text-[16px] font-semibold text-red-900">This looks like a bill that's already confirmed</h2>
          <p className="text-[14px] text-red-900/80" data-testid="duplicate-detail">
            Same {fields.length ? fields.join(", ") : "details"} as {dup.matches?.length || 1} confirmed bill{(dup.matches?.length || 1) > 1 ? "s" : ""}.
          </p>
        </div>
      </div>
      {!canOverride && <p className="text-[13px] text-red-900" data-testid="duplicate-no-permission">You don't have permission to confirm a duplicate.</p>}
      {canOverride && !open && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setOpen(true)} data-testid="duplicate-confirm-anyway"
            className="h-12 px-4 rounded-md border-2 border-red-400 bg-white text-[15px] font-semibold text-red-800">Confirm anyway</button>
          <button type="button" onClick={onDismiss} data-testid="duplicate-dismiss"
            className="h-12 px-4 rounded-md border border-[#E4E0D8] bg-white text-[15px]">Go back</button>
        </div>
      )}
      {canOverride && open && (
        <form onSubmit={submit} className="space-y-2" noValidate>
          <label htmlFor="dup-reason" className="block text-[14px] font-medium text-red-900">Why are you confirming this duplicate?</label>
          <textarea id="dup-reason" data-testid="duplicate-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            aria-invalid={!!err} aria-describedby={err ? "dup-reason-err" : undefined}
            className="w-full rounded-md border border-red-300 bg-white p-3 text-[16px] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400" />
          {err && <p id="dup-reason-err" className="text-[13px] text-red-800" data-testid="duplicate-reason-error">{err}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={busy || !reason.trim()} data-testid="duplicate-submit"
              className="h-12 px-5 rounded-md bg-red-700 text-white text-[15px] font-semibold disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Confirm duplicate"}
            </button>
            <button type="button" onClick={onDismiss} className="h-12 px-4 rounded-md border border-[#E4E0D8] bg-white text-[15px]" data-testid="duplicate-cancel">Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
};
